import { Observable, Subject, Subscription, switchMap, takeUntil, timer } from 'rxjs';
import type { SaveStateParams } from '../grid.types';
import { getAutoSaveGroupKey } from './grid-save.helpers';

export interface GridPostSaveCoordinatorConfig {
    getMethod: () => number | null;
    getDebounceTime: () => number;
    shouldRunOnSaveError: () => boolean;
    runDatatask: (method: number) => Promise<void>;
    afterRunFinished?: () => void;
}

export class GridPostSaveCoordinator {
    private readonly check$ = new Subject<number>();
    private readonly cancelCheck$ = new Subject<void>();

    private activeSaveCount = 0;
    private autoSaveQueued = false;
    private autoSaveQueueKeys = new Set<string>();
    private pasteSaveQueued = false;
    private hadSaveError = false;
    private pending = false;
    private running = false;
    private lastUserChange = 0;
    private editedCellOpen = false;

    constructor(private readonly config: GridPostSaveCoordinatorConfig) {}

    public createSubscription(destroy$: Observable<unknown>): Subscription {
        return this.check$
            .pipe(
                takeUntil(destroy$),
                switchMap((waitTime) => timer(waitTime).pipe(takeUntil(this.cancelCheck$))),
            )
            .subscribe(() => this.runIfReady());
    }

    public isRunning(): boolean {
        return this.running;
    }

    public recordUserChange(): void {
        this.lastUserChange = Date.now();

        if (this.pending === true) {
            this.scheduleCheck();
        }
    }

    public blockForOpenEditedCell(): void {
        if (this.config.getMethod() === null) return;

        this.editedCellOpen = true;
        this.cancelCheck();
    }

    public unblockForOpenEditedCell(): void {
        if (this.config.getMethod() === null) return;

        this.editedCellOpen = false;
        this.scheduleCheck();
    }

    public queueAutoSave(params: SaveStateParams): void {
        if (this.config.getMethod() === null) return;

        this.autoSaveQueueKeys.add(getAutoSaveGroupKey(params));
        this.autoSaveQueued = true;
        this.scheduleCheck();
    }

    public removeQueuedAutoSave(params: SaveStateParams): void {
        this.autoSaveQueueKeys.delete(getAutoSaveGroupKey(params));
        this.autoSaveQueued = this.autoSaveQueueKeys.size > 0;
    }

    public queuePasteSave(): void {
        if (this.config.getMethod() === null) return;

        this.pasteSaveQueued = true;
        this.scheduleCheck();
    }

    public clearQueuedPasteSave(): void {
        this.pasteSaveQueued = false;
    }

    public trackSaveStarted(): void {
        if (this.config.getMethod() === null) return;

        if (this.pending !== true) {
            this.hadSaveError = false;
        }

        this.pending = true;
        this.activeSaveCount++;

        this.cancelCheck();
    }

    public trackSaveFinished(): void {
        if (this.config.getMethod() === null) return;

        this.activeSaveCount = Math.max(0, this.activeSaveCount - 1);
        this.scheduleCheck();
    }

    public markSaveFailed(): void {
        if (this.config.getMethod() === null) return;

        this.hadSaveError = true;
    }

    public scheduleCheck(): void {
        this.cancelCheck();
        if (this.pending !== true || this.running === true || this.config.getMethod() === null) return;

        if (this.hasPendingActivity()) return;

        const debounceTime = this.config.getDebounceTime();
        const elapsedTime = this.lastUserChange > 0 ? Date.now() - this.lastUserChange : debounceTime;
        const waitTime = Math.max(0, debounceTime - elapsedTime);

        this.check$.next(waitTime);
    }

    public cancelCheck(): void {
        this.cancelCheck$.next();
    }

    public destroy(): void {
        this.cancelCheck();
        this.check$.complete();
        this.cancelCheck$.complete();
    }

    private hasQueuedSave(): boolean {
        return this.autoSaveQueued === true || this.pasteSaveQueued === true;
    }

    private hasPendingActivity(): boolean {
        return this.activeSaveCount > 0 || this.hasQueuedSave() || this.editedCellOpen === true;
    }

    private runIfReady(): void {
        const method = this.config.getMethod();

        if (method === null || this.pending !== true || this.running === true) return;

        if (this.hasPendingActivity()) {
            this.scheduleCheck();
            return;
        }

        const debounceTime = this.config.getDebounceTime();
        const elapsedTime = this.lastUserChange > 0 ? Date.now() - this.lastUserChange : debounceTime;

        if (elapsedTime < debounceTime) {
            this.scheduleCheck();
            return;
        }

        if (this.hadSaveError === true && this.config.shouldRunOnSaveError() === false) {
            this.pending = false;
            this.hadSaveError = false;
            return;
        }

        void this.runDatatask(method);
    }

    private async runDatatask(method: number): Promise<void> {
        this.running = true;
        this.pending = false;
        this.hadSaveError = false;

        try {
            await this.config.runDatatask(method);
        } finally {
            this.running = false;
            this.config.afterRunFinished?.();

            if ((this.pending as boolean) === true) {
                this.scheduleCheck();
            }
        }
    }
}
